import { useState, useEffect } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface DeleteUserDialogProps {
  user: {
    id: string;
    email: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (userId: string) => Promise<void>;
  isDeleting: boolean;
}

export const DeleteUserDialog = ({
  user,
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
}: DeleteUserDialogProps) => {
  const [confirmEmail, setConfirmEmail] = useState("");

  // Reset confirmation field when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setConfirmEmail("");
    }
  }, [open]);

  const displayName = user
    ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unknown"
    : "";

  const canDelete = confirmEmail.toLowerCase() === (user?.email || "").toLowerCase();

  const handleConfirm = async () => {
    if (!user || !canDelete) return;
    await onConfirm(user.id);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete User
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>
                You are about to permanently delete <strong>{displayName}</strong> ({user?.email}).
              </p>
              <div className="rounded-md bg-destructive/10 p-3 text-destructive">
                <p className="font-medium">This action cannot be undone!</p>
                <p className="mt-1 text-xs">
                  The following data will be permanently deleted:
                </p>
                <ul className="mt-2 list-inside list-disc text-xs">
                  <li>User profile and account</li>
                  <li>All tickets and bookings</li>
                  <li>Membership and credits</li>
                  <li>Referral history</li>
                </ul>
              </div>
              <div className="space-y-2 pt-2">
                <Label htmlFor="confirm-email">
                  Type <strong>{user?.email}</strong> to confirm:
                </Label>
                <Input
                  id="confirm-email"
                  value={confirmEmail}
                  onChange={(e) => setConfirmEmail(e.target.value)}
                  placeholder="Enter user's email"
                  autoComplete="off"
                />
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canDelete || isDeleting}
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete User
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};